import { useForm } from '@mantine/form';
import { mergeWith } from 'lodash';

import { useUpdateTransit } from '@hooks/transit';
import { TransitViewHouseFormValues } from './validateTransitViewHouseForm';

export const useTransitViewHouseFormInitializer = ({ transit }) => {
  const updateTransitMutation = useUpdateTransit();

  const buildFormValues = (t) => ({
    _id: t._id || '',
    name: t.name || '',
    generalInfo: {
      group: t.generalInfo.group || '',
      status: t.generalInfo.status || '',
    },
    TransitOperation: {
      declarationType: t.TransitOperation.declarationType || '',
      amendment: {
        amendmentTypeFlag: t.TransitOperation?.amendment?.amendmentTypeFlag,
      },
    },
    Consignment: {
      countryOfDispatch: t.Consignment?.countryOfDispatch || '',
      countryOfDestination: t.Consignment?.countryOfDestination || '',
      referenceNumberUCR: t.Consignment?.referenceNumberUCR || '',
      Consignor: {
        identificationNumber: t.Consignment?.Consignor?.identificationNumber || '',
        name: t.Consignment?.Consignor?.name || '',
        Address: {
          streetAndNumber: t.Consignment?.Consignor?.Address?.streetAndNumber || '',
          postcode: t.Consignment?.Consignor?.Address?.postcode || '',
          city: t.Consignment?.Consignor?.Address?.city || '',
          country: t.Consignment?.Consignor?.Address?.country || '',
        },
      },
      Consignee: {
        identificationNumber: t.Consignment?.Consignee?.identificationNumber || '',
        name: t.Consignment?.Consignee?.name || '',
        Address: {
          streetAndNumber: t.Consignment?.Consignee?.Address?.streetAndNumber || '',
          postcode: t.Consignment?.Consignee?.Address?.postcode || '',
          city: t.Consignment?.Consignee?.Address?.city || '',
          country: t.Consignment?.Consignee?.Address?.country || '',
        },
      },
      AdditionalSupplyChainActor: (t.Consignment?.AdditionalSupplyChainActor ?? []).map((additionalSupplyChainActor) => ({
        role: additionalSupplyChainActor.role || '',
        identificationNumber: additionalSupplyChainActor.identificationNumber || '',
      })),
      DepartureTransportMeans: (t.Consignment?.DepartureTransportMeans ?? []).map((departureTransportMeans) => ({
        typeOfIdentification: departureTransportMeans.typeOfIdentification || '',
        identificationNumber: departureTransportMeans.identificationNumber || '',
        nationality: departureTransportMeans.nationality || '',
      })),
      PreviousDocument: (t.Consignment?.PreviousDocument || []).map((previousDocument) => ({
        type: previousDocument.type || '',
        referenceNumber: previousDocument.referenceNumber || '',
        complementOfInformation: previousDocument.complementOfInformation || '',
      })),
      SupportingDocument: (t.Consignment?.SupportingDocument || []).map((previousDocument) => ({
        type: previousDocument.type || '',
        referenceNumber: previousDocument.referenceNumber || '',
        documentLineItemNumber: previousDocument.documentLineItemNumber || '',
        complementOfInformation: previousDocument.complementOfInformation || '',
      })),
      TransportDocument: (t.Consignment?.TransportDocument || []).map((previousDocument) => ({
        type: previousDocument.type || '',
        referenceNumber: previousDocument.referenceNumber || '',
      })),
      AdditionalReference: (t.Consignment?.AdditionalReference || []).map((previousDocument) => ({
        type: previousDocument.type || '',
        referenceNumber: previousDocument.referenceNumber || '',
      })),
      AdditionalInformation: (t.Consignment?.AdditionalInformation || []).map((previousDocument) => ({
        code: previousDocument.code || '',
        text: previousDocument.text || '',
      })),
      HouseConsignment: (t.Consignment?.HouseConsignment || []).map((houseConsignment) => ({
        isMissing: houseConsignment.isMissing || false,
        receivedFromUnloadPermission: houseConsignment.receivedFromUnloadPermission || false,
        sequenceNumber: houseConsignment.sequenceNumber || '',
        countryOfDispatch: houseConsignment.countryOfDispatch || '',
        countryOfDestination: houseConsignment.countryOfDestination || '',
        grossMass: houseConsignment.grossMass || '',
        referenceNumberUCR: houseConsignment.referenceNumberUCR || '',
        Consignor: {
          identificationNumber: houseConsignment.Consignor?.identificationNumber || '',
          name: houseConsignment.Consignor?.name || '',
          Address: {
            streetAndNumber: houseConsignment.Consignor?.Address?.streetAndNumber || '',
            postcode: houseConsignment.Consignor?.Address?.postcode || '',
            city: houseConsignment.Consignor?.Address?.city || '',
            country: houseConsignment.Consignor?.Address?.country || '',
          },
        },
        Consignee: {
          identificationNumber: houseConsignment.Consignee?.identificationNumber || '',
          name: houseConsignment.Consignee?.name || '',
          Address: {
            streetAndNumber: houseConsignment.Consignee?.Address?.streetAndNumber || '',
            postcode: houseConsignment.Consignee?.Address?.postcode || '',
            city: houseConsignment.Consignee?.Address?.city || '',
            country: houseConsignment.Consignee?.Address?.country || '',
          },
        },
        AdditionalSupplyChainActor: (houseConsignment.AdditionalSupplyChainActor ?? []).map((additionalSupplyChainActor) => ({
          role: additionalSupplyChainActor.role || '',
          identificationNumber: additionalSupplyChainActor.identificationNumber || '',
        })),
        DepartureTransportMeans: (houseConsignment.DepartureTransportMeans ?? []).map((departureTransportMeans) => ({
          typeOfIdentification: departureTransportMeans.typeOfIdentification || '',
          identificationNumber: departureTransportMeans.identificationNumber || '',
          nationality: departureTransportMeans.nationality || '',
        })),
        PreviousDocument: (houseConsignment.PreviousDocument || []).map((previousDocument) => ({
          type: previousDocument.type || '',
          referenceNumber: previousDocument.referenceNumber || '',
          complementOfInformation: previousDocument.complementOfInformation || '',
        })),
        SupportingDocument: (houseConsignment.SupportingDocument || []).map((previousDocument) => ({
          type: previousDocument.type || '',
          referenceNumber: previousDocument.referenceNumber || '',
          documentLineItemNumber: previousDocument.documentLineItemNumber || '',
          complementOfInformation: previousDocument.complementOfInformation || '',
        })),
        TransportDocument: (houseConsignment.TransportDocument || []).map((previousDocument) => ({
          type: previousDocument.type || '',
          referenceNumber: previousDocument.referenceNumber || '',
        })),
        AdditionalReference: (houseConsignment.AdditionalReference || []).map((previousDocument) => ({
          type: previousDocument.type || '',
          referenceNumber: previousDocument.referenceNumber || '',
        })),
        AdditionalInformation: (houseConsignment.AdditionalInformation || []).map((previousDocument) => ({
          code: previousDocument.code || '',
          text: previousDocument.text || '',
        })),
        batches: (houseConsignment.batches || []).map((batch) => ({
          id: batch.id || '',
          name: batch.name || '',
        })),
        ConsignmentItem: (houseConsignment.ConsignmentItem || []).map((consignmentItem) => ({
          isMissing: consignmentItem.isMissing || false,
          receivedFromUnloadPermission: consignmentItem.receivedFromUnloadPermission || false,
          goodsItemNumber: consignmentItem.goodsItemNumber || '',
          declarationGoodsItemNumber: consignmentItem.declarationGoodsItemNumber || '',
          declarationType: consignmentItem.declarationType || '',
          countryOfDispatch: consignmentItem.countryOfDispatch || '',
          countryOfDestination: consignmentItem.countryOfDestination || '',
          referenceNumberUCR: consignmentItem.referenceNumberUCR || '',
          Commodity: {
            descriptionOfGoods: consignmentItem.Commodity?.descriptionOfGoods || '',
            CUSCode: consignmentItem.Commodity?.CUSCode || '',
            DangerousGoods: (consignmentItem.Commodity?.DangerousGoods ?? []).map((dangerousGoods) => ({
              UNNumber: dangerousGoods.UNNumber || '',
            })),
            CommodityCode: {
              hsCode: consignmentItem.Commodity?.CommodityCode?.hsCode || '',
              initialAmountToPayForDuties: consignmentItem.Commodity?.CommodityCode?.initialAmountToPayForDuties || '',
              initialAmountToPayForVat: consignmentItem.Commodity?.CommodityCode?.initialAmountToPayForVat || '',
            },
            GoodsMeasure: {
              grossMass: consignmentItem.Commodity?.GoodsMeasure?.grossMass || '',
              netMass: consignmentItem.Commodity?.GoodsMeasure?.netMass || '',
              supplementaryUnits: consignmentItem.Commodity?.GoodsMeasure?.supplementaryUnits || '',
            },
          },
          Packaging: (consignmentItem.Packaging ?? []).map((packaging) => ({
            typeOfPackages: packaging.typeOfPackages || '',
            numberOfPackages: packaging.numberOfPackages || '',
            shippingMarks: packaging.shippingMarks || '',
          })),
          AdditionalSupplyChainActor: (consignmentItem.AdditionalSupplyChainActor ?? []).map((additionalSupplyChainActor) => ({
            role: additionalSupplyChainActor.role || '',
            identificationNumber: additionalSupplyChainActor.identificationNumber || '',
          })),
          PreviousDocument: (consignmentItem.PreviousDocument || []).map((previousDocument) => ({
            type: previousDocument.type || '',
            referenceNumber: previousDocument.referenceNumber || '',
            goodsItemNumber: previousDocument.goodsItemNumber || '',
            typeOfPackages: previousDocument.typeOfPackages || '',
            numberOfPackages: previousDocument.numberOfPackages || '',
            measurementUnitAndQualifier: previousDocument.measurementUnitAndQualifier || '',
            quantity: previousDocument.quantity || '',
            complementOfInformation: previousDocument.complementOfInformation || '',
          })),
          SupportingDocument: (consignmentItem.SupportingDocument || []).map((previousDocument) => ({
            type: previousDocument.type || '',
            referenceNumber: previousDocument.referenceNumber || '',
            documentLineItemNumber: previousDocument.documentLineItemNumber || '',
            complementOfInformation: previousDocument.complementOfInformation || '',
          })),
          AdditionalReference: (consignmentItem.AdditionalReference || []).map((previousDocument) => ({
            type: previousDocument.type || '',
            referenceNumber: previousDocument.referenceNumber || '',
          })),
          AdditionalInformation: (consignmentItem.AdditionalInformation || []).map((previousDocument) => ({
            code: previousDocument.code || '',
            text: previousDocument.text || '',
          })),
        })),
      })),
    },
  });

  const form = useForm<TransitViewHouseFormValues>({
    initialValues: buildFormValues(transit),
  });

  const onSubmit = async (values) => {
    updateTransitMutation.mutate({
      transit: mergeWith({ _id: transit._id }, values, (objValue, srcValue) => (Array.isArray(srcValue) ? srcValue : undefined)),
    });
  };

  return { buildFormValues, form, onSubmit };
};
